<script setup lang="ts">
import { router, usePage } from '@inertiajs/vue3';
import { Check, Languages } from 'lucide-vue-next';
import { computed } from 'vue';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { update } from '@/routes/locale';

const page = usePage();
const locale = computed(() => (page.props.locale as string) ?? 'fr');
const locales = computed(
    () => (page.props.locales as string[]) ?? ['fr', 'en'],
);

const labels: Record<string, string> = {
    fr: 'Français',
    en: 'English',
};

const currentLabel = computed(() => labels[locale.value] ?? locale.value);

const switchLocale = (code: string) => {
    if (code === locale.value) {
        return;
    }

    router.post(
        update(),
        { locale: code },
        { preserveScroll: true, preserveState: true },
    );
};
</script>

<template>
    <DropdownMenu>
        <DropdownMenuTrigger :as-child="true">
            <Button variant="ghost" size="sm" class="cursor-pointer gap-2">
                <Languages class="size-4" />
                <span class="hidden sm:inline">{{ currentLabel }}</span>
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-40">
            <DropdownMenuLabel class="text-xs text-muted-foreground">
                Language
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
                v-for="code in locales"
                :key="code"
                class="cursor-pointer"
                :disabled="code === locale"
                @click="switchLocale(code)"
            >
                <span class="flex w-full items-center justify-between">
                    {{ labels[code] ?? code }}
                    <Check v-if="code === locale" class="size-4 text-primary" />
                </span>
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
